import { omniaResolve } from "@omnia/shared/promises"
import { convertDatesToJsonUtc, createLookup, IOmniaSession, isArrayEmpty, OmniaModuleId, OmniaService, pad2, setDates, sortDates, toDate } from "../omnia"
import { IDatalayersService, IFieldsService } from "../types"
import { isRgb } from "./day"
import { ImageryProviderId } from "./providers"
import { Boundary, Imagery, ImageryDate, ImageryProvider, SatImageType } from "./types"

interface ServiceData {
    boundaries: Boundary[]
    providers: ImageryProvider[]
}

export class SatelliteApi extends OmniaService {


    private _boundaries: Boundary[] = []
    private _providers: ImageryProvider[] = []

    private _imagery: { [key: string]: Imagery[] } = {}

    public readonly canViewClouds: boolean = false

    constructor(session: IOmniaSession, private readonly fieldSvc: IFieldsService, private readonly layerSvc: IDatalayersService) {

        super(session, {
            name: "SatelliteApi",
            module: OmniaModuleId.Layers,
            dependencies: [fieldSvc]
        })

        this.canViewClouds = session.permissions?.satellite?.viewClouds ?? false

    }

    refresh(auto = true): ng.IPromise<any> {
        return super.refreshInternal(!auto)
    }

    protected getRefreshPromise() {

        const { yearId } = this

        const url = `/api/satellite/v2?year=${yearId}`

        return this.apiInterface.get(url).then((d: ServiceData) => {

            this._imagery = {}
            this._boundaries = d.boundaries ?? []
            this._providers = setupProviders(d.providers)
        })

    }

    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Layers)
                || (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Satellite)
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }

    setupFieldBoundaries(fields: { fieldId: number; inactive: boolean }[]) {
        const url = '/api/satellite/v1/boundaries'
        return this.apiInterface.post(url, fields)
    }

    getProviders() {
        return this._providers.map(p => p.providerId)
    }

    getProvider(provider: ImageryProviderId) {
        return this._providers.find(p => p.providerId === provider)
    }

    getTypes(providerId: ImageryProviderId) {


        const providerTypes = new Set(this._providers
            .filter(p => p.providerId === providerId)
            .flatMap(p => p.dates.map(d => d.type))
        )

        let types = [
            SatImageType.RGB,
            SatImageType.PlanetRGB,
            SatImageType.NDVI,
            SatImageType.PlanetNDVI,
            SatImageType.CHI,
            SatImageType.PlanetCHI,
            SatImageType.EVI,
        ]
        .filter(t => providerTypes.has(t))

        if (isArrayEmpty(types)) {
            types = [SatImageType.RGB]
        }

        return types

    }

    getFields() {
        return this.fieldSvc.getFields(false)
    }

    getCropping() {
        return this.fieldSvc.cropping.filter(c => c.currentYear && c.visible)
    }

    getBoundaries() {
        return [...this._boundaries]
    }

    getDates(providerId: ImageryProviderId, type: SatImageType) {

        const provider = this._providers.find(p => p.providerId === providerId)
        const dates = provider?.dates ?? []

        const lookup = createLookup(dates, d => d.date.valueOf())

        return dates
            .filter(d => d.type == type)
            .map(d => {

                const types = lookup[d.date.valueOf()]
                const rgbOnly = types?.every(d => isRgb(d.type)) ?? false

                return ({
                    ...d,
                    rgbOnly
                })

            })

    }

    getImageryOnDate(providerId: ImageryProviderId, type: SatImageType, date: Date) {

        const dateText = [
            date.getFullYear(),
            pad2(date.getMonth() + 1),
            pad2(date.getDate())
        ].join("-")

        const key = providerId + ":" + type + ":" + dateText

        const p = this._imagery[key]

        if (p) {
            return omniaResolve(p)
        }

        const url = `/api/satellite/v1/provider/${providerId}/type/${type}/date/${dateText}`

        return this.apiInterface.get(url).then((data: Imagery[]) => {

            const previews = setDates(data || [], ["date"], true)
            this._imagery[key] = previews

            return previews

        })

    }

    getLayers(ids: number[]) {
        return this.layerSvc.getLayers(ids, true)
    }

    requestLayerCreation(providerId: ImageryProviderId, imageType: SatImageType, date: Date, croppingIds: number[]) {

        const data = {
            providerId,
            imageType,
            date: convertDatesToJsonUtc(date),
            croppingIds
        }

        const url = `/api/satellite/v1/layers`

        return this.apiInterface.put(url, data)

    }

}


function setupProviders(providers: ImageryProvider[]) {

    return (providers ?? []).map(p => ({
        ...p,
        dates: setupDates(p.dates)
    }))

}

function setupDates(dates: ImageryDate[]) {

    dates = (dates || [])
        .map(d => ({
            ...d,
            date: toDate(d.date, true)
        }))
        .sort((a, b) => sortDates(a.date, b.date))

    return dates

}
